import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authService } from '../services/authService';

// Chat slides data
const chatSlides = [
  {
    user: 'Priori, can you run a model to show how ads spending impacts our sales?',
    response: [
      'Of course! Let\'s take a look.',
      { type: 'img', src: '/img/ChatImages/chat6.png', alt: 'Regression Chart' }
    ]
  },
    {
    user: 'Priori, can you analyze PM2.5 levels over time and predict future values?',
    response: [
        'Sure! Here is the analysis along with predicted values:',
        { type: 'img', src: '/img/ChatImages/prediction.png', alt: 'PM2.5 Predictions' }
    ]
    },
    {
  user: "Priori, can you model the probability of developing Alzheimer’s disease based on relevant biomarkers using Bayesian inference?",
  response: [
    "Absolutely! Below is the Bayesian analysis along with the resulting posterior distributions:",
    { type: 'img', src: '/img/ChatImages/post.png', alt: "Posterior distributions for Alzheimer's probability" }
  ]
}
];

// ChatSlideshow component
const ChatSlideshow = () => {
  const [current, setCurrent] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const [animationKey, setAnimationKey] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      // Start fade out
      setIsVisible(false);
      
      // After fade out completes, change slide and restart animations
      setTimeout(() => {
        setCurrent((prev) => (prev + 1) % chatSlides.length);
        setAnimationKey(prev => prev + 1); // Force animation restart
        setIsVisible(true);
      }, 400); // 400ms fade out duration
    }, 5500); // Change slide every 5 seconds
    return () => clearInterval(timer);
  }, []);

  const slide = chatSlides[current];

  const slideTitles = [
    'Analytics',
    'Time Series Forecasting',
    'Bayesian Inference'
  ];

  return (
    <div className="chat-slideshow-container">
      <h2 
        key={`title-${animationKey}`}
        className={`chat-slide-title ${isVisible ? 'visible' : 'fade-out'}`}
      >
        {slideTitles[current]}
      </h2>
      <div 
        key={`user-${animationKey}`} 
        className={`chat-bubble signup-chat-bubble ${isVisible ? 'visible' : 'fade-out'}`}
      >
        <span>{slide.user}</span>
      </div>
      <div 
        key={`response-${animationKey}`} 
        className={`chat-bubble-response signup-chat-response ${isVisible ? 'visible' : 'fade-out'}`}
      >
        {slide.response.map((r, i) =>
          typeof r === 'string'
            ? <span className="response-text" key={i}>{r}</span>
            : r.type === 'img'
              ? <div key={i}><img src={r.src} alt={r.alt} /></div>
              : null
        )}
      </div>
    </div>
  );
};

const SignupPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswordFields, setShowPasswordFields] = useState(false);
  const [animatePasswordFields, setAnimatePasswordFields] = useState(false);
  const [isLogin, setIsLogin] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Check URL params or state to determine if this is login mode
  useEffect(() => {
    const isLoginMode = location.pathname === '/login' || 
                       location.state?.mode === 'login' ||
                       new URLSearchParams(location.search).get('mode') === 'login';
    setIsLogin(isLoginMode);
  }, [location]);

  // Trigger animation when password fields are shown
  useEffect(() => {
    if (showPasswordFields) {
      // Small delay to ensure smooth animation
      setTimeout(() => {
        setAnimatePasswordFields(true);
      }, 50);
    } else {
      setAnimatePasswordFields(false);
    }
  }, [showPasswordFields]);

  const handleGoogleAuth = async () => {
    setIsLoading(true);
    setError('');
    
    try {
      const result = await authService.signInWithGoogle();
      
      if (result.success) {
        // Redirect to main app or dashboard
        navigate('/platform');
      } else {
        setError(result.error);
      }
    } catch (error) {
      setError('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const validateEmail = (email) => {
    // Simple email regex
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleEmailAuth = async () => {
    // Validate email for both login and signup
    if (!email) {
      setError('Please enter your email address');
      return;
    }
    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }
    // For signup mode, if password fields aren't shown yet, show them
    if (!isLogin && !showPasswordFields) {
      setShowPasswordFields(true);
      setError('');
      return;
    }

    if (!email || (!isLogin && (!password || !confirmPassword))) {
      setError('Please fill in all fields');
      return;
    }

    // For signup, check if passwords match
    if (!isLogin && password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsLoading(true);
    setError('');
    setSuccessMessage('');
    
    try {
      let result;
      
      if (isLogin) {
        if (!password) {
          setError('Please enter your password');
          setIsLoading(false);
          return;
        }
        result = await authService.signInWithEmail(email, password);
      } else {
        // For signup, we can handle password creation separately or redirect
        // For now, let's create a simple password requirement
        if (!password) {
          setError('Please enter a password');
          setIsLoading(false);
          return;
        }
        result = await authService.signUpWithEmail(email, password);
      }
      
      if (result.success) {
        if (isLogin) {
          // Redirect to main app
          navigate('/platform');
        } else {
          // Show success message for signup
          setSuccessMessage('Account created! Please check your email to verify your account.');
          setEmail('');
          setPassword('');
          setConfirmPassword('');
          setShowPasswordFields(false);
          setAnimatePasswordFields(false);
        }
      } else {
        setError(result.error);
      }
    } catch (error) {
      setError('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleMode = () => {
    setIsLogin(!isLogin);
    setError('');
    setSuccessMessage('');
    setPassword('');
    setConfirmPassword('');
    setShowPasswordFields(false);
    setAnimatePasswordFields(false);
  };

  return (
    <div className="signup-page">
      <div className="signup-container">
        {/* Left side - Hero content and signup form */}
        <div className="signup-hero">
          <div className="signup-hero-content">
            <h1 className="signup-hero-title">
              Your data, <br />
              working smarter for you.
            </h1>
            <p className="signup-hero-subtitle">
              {isLogin ? 'Welcome back! Sign in to continue' : 'Analyze, predict, and make confident decisions'}
            </p>
          </div>

          {/* Auth form */}
          <div className="signup-form">
            {error && <div className="auth-error">{error}</div>}
            {successMessage && <div className="auth-success">{successMessage}</div>}
            
            <button 
              onClick={handleGoogleAuth} 
              className="google-signup-btn"
              disabled={isLoading}
            >
              <svg width="18" height="18" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              {isLoading ? 'Loading...' : `Continue with Google`}
            </button>

            <div className="signup-divider">
              <span>OR</span>
            </div>

            <div className="email-input-container">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="email-input"
                disabled={isLoading}
              />
            </div>

            {(isLogin || showPasswordFields) && (
              <div className={`password-field-container ${(isLogin || animatePasswordFields) ? 'show' : ''}`}>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="email-input"
                  disabled={isLoading}
                />
              </div>
            )}

            {(!isLogin && showPasswordFields) && (
              <div className={`password-field-container ${animatePasswordFields ? 'show' : ''}`}>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password"
                  className="email-input"
                  disabled={isLoading}
                />
              </div>
            )}

            <button 
              onClick={handleEmailAuth} 
              className="email-signup-btn"
              disabled={isLoading}
            >
              {isLoading ? 'Loading...' : 
               isLogin ? 'Sign In' : 
               showPasswordFields ? 'Create Account' : 'Continue with email'}
            </button>

            <button 
              onClick={toggleMode}
              className="toggle-mode-btn"
              disabled={isLoading}
            >
              {isLogin ? "Don't have an account? Sign up" : "Already have an account? Sign in"}
            </button>

            <p className="privacy-notice">
              By continuing, you acknowledge Priori's <a href="#" className="privacy-link">Privacy Policy</a>.
            </p>
          </div>
        </div>

        {/* Right side - Chat preview and images */}
        <div className="signup-form-section">
          <div className="signup-chat-preview">
            <ChatSlideshow />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;
